import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, ThermometerSnowflake, Thermometer, GaugeCircle, Star, Recycle, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';
import { Starter, STARTER_TEXTURES, MANAGEMENT_STRATEGIES } from '../../types/baking';
import { checkStarterHealth, checkAndNotifyStarterFeeding } from '../../lib/utils';
import { auth, db } from '../../lib/firebase';
import { StarterDetailsModal } from '../starter/StarterDetailsModal';
import { StarterActivityGraph } from '../starter/StarterActivityGraph';
import { updateStarter } from '../../lib/store';
import { getCurrentActivity, RATIO_TO_PROFILE_MAP, ACTIVITY_PROFILES } from '../starter/StarterUtils';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';

interface ActiveStarterProps {
  starter?: Starter;
  onNavigateToStarter?: () => void;
}

export function ActiveStarter({ starter, onNavigateToStarter }: ActiveStarterProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [recentlyFed, setRecentlyFed] = useState(false);
  const [starterImage, setStarterImage] = useState<string>('');
  const lastCheckRef = useRef<number>(Date.now());
  const imageUpdateInterval = useRef<NodeJS.Timeout | null>(null);
  const [activityTrend, setActivityTrend] = useState<'up' | 'down' | 'stable'>('stable');

  // Update starter image based on activity level
  useEffect(() => {
    if (!starter) return;
    
    const updateStarterImage = () => {
      const hoursSinceLastFeed = 
        (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
      
      const currentActivity = getCurrentActivity(
        hoursSinceLastFeed,
        starter.storage,
        starter.roomTemperature, 
        starter.feedingRatio
      );
      
      setStarterImage(getStarterImage(currentActivity));
      
      // Determine activity trend
      const profileKey = RATIO_TO_PROFILE_MAP[starter.feedingRatio] || '1-1-1';
      const profile = ACTIVITY_PROFILES[profileKey];
      const activityProfile = starter.storage === 'room' ? profile.room : profile.fridge;
      
      // If recently fed (less than the time to first peak), trend is up
      const timeToFirstPeak = activityProfile.find(p => p.percentage === 100)?.hours || 0;
      
      if (hoursSinceLastFeed < timeToFirstPeak) {
        setActivityTrend('up');
      } else {
        // Find the plateau threshold (when activity bottoms out)
        const lastPoint = activityProfile[activityProfile.length - 1];
        const plateauThreshold = lastPoint.hours;
        const minActivity = lastPoint.percentage;
        
        if (hoursSinceLastFeed >= plateauThreshold && currentActivity <= minActivity + 5) {
          // We're at the plateau phase
          setActivityTrend('stable');
        } else {
          // We're in the declining phase
          setActivityTrend('down');
        }
      }
    };
    
    // Update image immediately
    updateStarterImage();
    
    // Set up interval to update image every minute
    imageUpdateInterval.current = setInterval(updateStarterImage, 60000);
    
    // Clean up interval on unmount or when starter changes
    return () => {
      if (imageUpdateInterval.current) {
        clearInterval(imageUpdateInterval.current);
      }
    };
  }, [starter]);

  useEffect(() => {
    if (!starter || !auth.currentUser) return;

    const checkStarter = async () => {
      try {
        const now = Date.now();
        if (now - lastCheckRef.current < 60 * 60 * 1000) {
          return;
        }
        lastCheckRef.current = now;

        // Notification creation moved to backend (checkStarterFeedings.ts)
      } catch (error) {
        console.error('Error checking starter status:', error);
      }
    };

    checkStarter();
    const interval = setInterval(checkStarter, 60 * 60 * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [starter]);

  // Check if starter was recently fed (less than 4 hours ago)
  useEffect(() => {
    if (!starter) return;
    
    const hoursSinceLastFeed = getHoursSinceLastFeed();
    setRecentlyFed(hoursSinceLastFeed < 4);
    
    // Set up timer to re-enable feed button after 4 hours
    if (hoursSinceLastFeed < 4) {
      const timeUntilFeedable = (4 - hoursSinceLastFeed) * 60 * 60 * 1000;
      const timer = setTimeout(() => {
        setRecentlyFed(false);
      }, timeUntilFeedable);
      
      return () => clearTimeout(timer);
    }
  }, [starter]);

  // Render the appropriate trend icon
  const renderTrendIcon = () => {
    switch (activityTrend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-400 ml-1" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-amber-400 ml-1" />;
      case 'stable':
        return <Minus className="w-4 h-4 text-gray-400 ml-1" />;
    }
  };

  if (!starter) {
    return (
      <div className="h-[500px] bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-5 h-5 text-yellow-500 fill-yellow-500"/>
          <h2 className="text-2xl text-white font-brand font-light italic">My Super Starter</h2>
        </div>
        <div className="flex flex-col items-center justify-center h-[calc(100%-4rem)]">
          <p className="text-white text-center">
            No star starter yet.
          </p>
          <p className="text-white text-center mb-6 flex items-center gap-1">
            Mark a starter <Star className="w-5 h-5 text-yellow-500 fill-yellow-500 inline"/> on the Starters page to track it here.
          </p>
          {onNavigateToStarter && (
            <button
              onClick={onNavigateToStarter}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
            >
              GO TO STARTERS
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }

  const health = checkStarterHealth(starter);
  const textureInfo = STARTER_TEXTURES.find(t => t.id === starter.texture);
  const StorageIcon = starter.storage === 'fridge' 
    ? ThermometerSnowflake 
    : starter.roomTemperature 
    ? GaugeCircle 
    : Thermometer;
  const strategy = MANAGEMENT_STRATEGIES[starter.managementStrategy || 'normal'];

  const getHoursSinceLastFeed = () => {
    if (!starter.lastFed) return 0;
    return (new Date().getTime() - new Date(starter.lastFed).getTime()) / (1000 * 60 * 60);
  };

  const getMaxFeedingInterval = () => {
    return starter.storage === 'fridge'
      ? strategy.feedingInterval.fridge
      : strategy.feedingInterval.room;
  };

  const getNextFeedingTime = () => {
    if (!starter.lastFed) return null;

    const lastFed = new Date(starter.lastFed);
    const nextFeeding = new Date(lastFed);
    const feedingInterval = getMaxFeedingInterval();
    
    nextFeeding.setHours(nextFeeding.getHours() + feedingInterval);

    const now = new Date();
    const diff = nextFeeding.getTime() - now.getTime();
    
    if (diff <= 0) return 'Overdue';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    
    return `${hours}h ${minutes}m`;
  };

  const handleFeed = async () => {
    if (recentlyFed) return;

    try {
      const now = new Date();
      await updateStarter(starter.id, { lastFed: now });
      setRecentlyFed(true);

      // Mark all unprocessed notifications for this starter as processed
      if (auth.currentUser) {
        const notificationsRef = collection(db, 'notifications');
        const q = query(
          notificationsRef,
          where('userId', '==', auth.currentUser.uid),
          where('type', '==', 'starter_feeding'),
          where('data.starterId', '==', starter.id),
          where('processed', '==', false)
        );

        const notificationDocs = await getDocs(q);
        const updatePromises = notificationDocs.docs.map(notificationDoc =>
          updateDoc(doc(db, 'notifications', notificationDoc.id), { processed: true })
        );

        await Promise.all(updatePromises);
        console.log(`Marked ${notificationDocs.size} notifications as processed for starter ${starter.id}`);
      }

      // Reset the recently fed status after 4 hours
      setTimeout(() => {
        setRecentlyFed(false);
      }, 4 * 60 * 60 * 1000);
    } catch (error) {
      console.error('Error feeding starter:', error);
    }
  };

  const nextFeeding = getNextFeedingTime();
  const hoursSinceLastFeed = getHoursSinceLastFeed();

  // Helper function to get the appropriate starter image based on activity level
  const getStarterImage = (activityLevel: number) => {
    if (activityLevel <= 20) return '/starter/starter10.png';
    if (activityLevel <= 50) return '/starter/starter30.png';
    if (activityLevel <= 80) return '/starter/starter60.png';
    return '/starter/starter100.png';
  };

  return (
    <>
      <div 
        className="h-[800px] sm:h-[500px] bg-gradient-to-br from-[#1C1C1C] to-[#888888] shadow-md cursor-pointer transition-shadow hover:border hover:border-amber-400 hover:shadow-xl"
        onClick={() => setShowDetails(true)}
      >
        <div className="flex flex-col-reverse sm:flex-row justify-between items-start h-full">
          {/* Left side - Starter info */}
          <div className="flex-1 w-full lg:max-w-[50%] h-full p-6 flex flex-col">
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <h2 className="text-2xl text-white font-brand font-light italic">{starter.name}</h2>
              </div> 
              
              <div className="w-full min-w-[120px]">
                <StarterActivityGraph
                  hoursSinceLastFeed={hoursSinceLastFeed}
                  storage={starter.storage}
                  roomTemperature={starter.roomTemperature}
                  feedingRatio={starter.feedingRatio}
                  className="origin-right"
                />
              </div>
              
              <div className="flex items-center gap-2 mt-1 mb-4">
                <span className="text-sm text-white">{textureInfo?.label}</span>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-white">{starter.feedingRatio ? starter.feedingRatio.replace(/-/g, ':') : '1:1:1'}</span>
                <span className="text-gray-400">•</span>
                <span className="flex items-center gap-1 text-sm text-white">
                  <StorageIcon className="w-4 h-4" />
                  {starter.storage === 'fridge' 
                    ? 'Refrigerated' 
                    : starter.roomTemperature 
                    ? `${starter.roomTemperature}°C`
                    : 'Room temp.'}
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-white">Next feeding in</p>
                  <div className="flex items-center">
                    <p className="text-2xl text-amber-400 font-brand font-light italic">{nextFeeding}</p>
                  </div>
                </div>

                {health.status !== 'healthy' && (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-1 flex-shrink-0 text-amber-400" />
                    <span className="inline-block px-2 py-1 bg-[#F15A24] text-white rounded-lg text-sm">
                      Starter needs immediate feeding
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Feed button at bottom */}
            <div className="flex justify-end mt-auto">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFeed();
                }}
                disabled={recentlyFed}
                className={`flex items-center gap-2 px-3 py-1 rounded-xl border border-white text-sm font-bold uppercase transition-all duration-300 ${
                  recentlyFed
                    ? 'text-gray-200 opacity-50 cursor-not-allowed'
                    : 'text-white hover:bg-amber-500 hover:border-amber-500'
                }`}
              >
                <Recycle className="w-4 h-4" />
                {recentlyFed ? 'Recently Fed' : 'Feed Now'}
              </button>
            </div>
          </div>

          {/* Right side - Starter jar visualization */}
          <div className="flex-1 w-full lg:max-w-[50%] h-full relative h-[500px] lg:h-full">
            <img
              src={starterImage || '/starter/starter10.png'} // Fallback image if not yet set
              alt="Starter jar visualization"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div className="hidden sm:block absolute inset-y-0 left-0 w-[5px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
            <div className="hidden sm:block absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
          </div>
        </div>
      </div>

      {showDetails && (
        <StarterDetailsModal
          starter={starter}
          starters={[starter]}
          onClose={() => setShowDetails(false)}
        />
      )}
    </>
  );
}